/** The application shell — topbar, sidebar, content.
 *
 * UI_TASTE.md Section A opens with the rule that makes this different from
 * the CRM's shell: the ERP topbar MUST show the active company and financial
 * year, persistently. A user has to be able to see which books they are
 * working in. Getting that wrong is not a cosmetic error — it is how someone
 * posts a purchase order into the wrong entity.
 */

import { Fragment, useEffect, useState, type ReactNode } from 'react'
import type { Session } from '@/types'
import { useRights } from '@/lib/rights'
import { canView } from '@/screens/access'

interface NavItem {
  label: string
  screen: string
  count?: number
  /** A section heading INSIDE a group — the masters that module owns, shown
   *  beneath its documents rather than in a separate top-level Masters menu. */
  divider?: string
  /** Not built yet. Rendered, but inert and visibly so. */
  soon?: boolean
}
interface NavGroup { label: string; items: NavItem[] }

/** Grouped by MODULE, with each module's own masters beneath its documents.
 *
 * GHERP split the whole system into Masters and Transactions — a filing
 * cabinet's shape, not a working day's. Somebody doing purchasing visited two
 * different top-level menus all day, and `Item Master` lived under *Design*
 * although stores, purchase and sales used it constantly. Its Masters menu
 * ended in a `Miscellaneous` holding eleven unrelated screens, which is what a
 * taxonomy looks like once it has run out.
 *
 * So: one place per job, and the reference data a module owns sits next to
 * the work that uses it. Nothing is `Miscellaneous` — every screen has an
 * owning module, which forces "who owns this?" to be answered rather than
 * deferred.
 *
 * **Screens marked `soon` are not built.** They are shown because the shape of
 * the system is useful to see, and inert because a menu that leads nowhere
 * wastes the reader's time. Each stops being `soon` as it lands.
 *
 * Spares and Service — whole transaction groups in GHERP — are deliberately
 * absent. FKMT parked them on 23 August 2026; they return when needed rather
 * than existing because they once existed.
 */
export const NAV: NavGroup[] = [
  // First, deliberately: "what is waiting for me?" is the question people
  // arrive with. GHERP made them check six separate screens for the answer.
  { label: 'My Work', items: [
    { label: 'Approvals', screen: 'approvals' },
  ]},

  { label: 'Purchase', items: [
    { label: 'Purchase Orders',  screen: 'purchase/po',      soon: true },
    { label: 'Purchase Invoices', screen: 'purchase/invoice', soon: true },
    { divider: 'Masters', label: 'Suppliers', screen: 'masters/suppliers' },
  ]},

  { label: 'Stores', items: [
    { label: 'Goods Receipts',   screen: 'stores/grn' },
    { label: 'Material Issues',  screen: 'stores/issue' },
    { label: 'Stock Transfers',  screen: 'stores/transfer' },
    { label: 'Stock Balance',    screen: 'stores/balance' },
    { divider: 'Masters', label: 'Locations', screen: 'masters/locations' },
  ]},

  { label: 'Sales', items: [
    { label: 'Quotations',  screen: 'sales/quotation', soon: true },
    { label: 'Sales Orders', screen: 'sales/so',       soon: true },
    { label: 'Invoices',    screen: 'sales/invoice',   soon: true },
    { divider: 'Masters', label: 'Customers', screen: 'masters/customers' },
  ]},

  { label: 'Design', items: [
    { label: 'PBOM', screen: 'design/pbom', soon: true },
    { label: 'PSL',  screen: 'design/psl',  soon: true },
    { label: 'Item Revisions', screen: 'design/item-revisions' },
    { divider: 'Masters', label: 'Items', screen: 'masters/items' },
    { label: 'Item Groups',      screen: 'masters/item-groups' },
    { label: 'Item Sub-Groups',  screen: 'masters/item-subgroups' },
    { label: 'Product Families', screen: 'masters/product-families' },
    { label: 'Equipment Models', screen: 'masters/product-models' },
    { label: 'Units of Measure', screen: 'masters/uom' },
    { label: 'Bill of Material', screen: 'masters/bom' },
  ]},

  { label: 'Production', items: [
    { label: 'Work Orders', screen: 'production/work_order', soon: true },
    { label: 'Job Work Orders', screen: 'jobwork/order' },
    { divider: 'Masters', label: 'Processes', screen: 'masters/processes' },
    { label: 'Item Routes', screen: 'masters/item-routes' },
    { label: 'Stages',      screen: 'masters/stages' },
  ]},

  { label: 'Quality', items: [
    { label: 'Inspections', screen: 'quality/inspection', soon: true },
  ]},

  { label: 'Projects', items: [
    { label: 'Projects', screen: 'masters/projects', soon: true },
  ]},

  { label: 'Accounts', items: [
    { label: 'Vouchers', screen: 'accounts/voucher', soon: true },
    { divider: 'Masters', label: 'GST Rates', screen: 'masters/tax-rates' },
    { label: 'Currency', screen: 'masters/currency', soon: true },
  ]},

  { label: 'Administration', items: [
    { label: 'Companies',       screen: 'admin/companies' },
    { label: 'Financial Years', screen: 'admin/financial-years' },
    { label: 'Company Access',  screen: 'admin/company-access' },
    { label: 'Users',           screen: 'admin/users' },
    { label: 'Roles',           screen: 'admin/roles' },
    { label: 'Rights',          screen: 'admin/rights' },
    { label: 'Audit Log',       screen: 'admin/audit', soon: true },
  ]},
]

interface Props {
  session: Session
  screen: string
  onNavigate: (screen: string) => void
  onSwitchContext: () => void
  onSignOut: () => void
  children: ReactNode
}

export function Shell(
  { session, screen, onNavigate, onSwitchContext, onSignOut, children }: Props,
) {
  const [drawerOpen, setDrawerOpen] = useState(false)

  // The menu shows only what this person may open. Screens not built yet
  // stay, inert: they show the shape of the system and no data. Until the
  // rights have arrived nothing is listed, so no entry appears and then
  // vanishes. RIGHTS_MODEL.md 6.1, UI_TASTE.md.
  const rights = useRights()
  const nav = NAV
    .map(g => ({ ...g, items: g.items.filter(
      i => i.soon || canView(i.screen, rights) !== false) }))
    .filter(g => g.items.length > 0)

  // One group open at a time. The sidebar carries nine modules now and will
  // carry more; showing every screen at once is a list to read rather than a
  // menu to use. Seeded from wherever the person already is, so arriving on a
  // screen never leaves its own group shut.
  const groupOf = (scr: string) =>
    NAV.find(g => g.items.some(i => i.screen === scr))?.label
  const [openGroup, setOpenGroup] = useState<string | undefined>(
    () => groupOf(screen) ?? 'My Work')

  // Following a link from elsewhere - a record reference, the hash - should
  // open the group that link landed in.
  useEffect(() => {
    const g = groupOf(screen)
    if (g) setOpenGroup(g)
  }, [screen])

  // The drawer closes on any navigation — UI_TASTE.md's mobile rule.
  useEffect(() => { setDrawerOpen(false) }, [screen])

  const initials = session.name
    .split(' ').filter(Boolean).slice(0, 2)
    .map(p => p[0]?.toUpperCase()).join('')

  const yearClosed = session.yearStatus === 'Closed'

  return (
    <div className="shell">
      <header className="topbar">
        <button
          className="btn btn-secondary mobile-only"
          aria-label="Open navigation"
          onClick={() => setDrawerOpen(o => !o)}
        >☰</button>

        <div className="topbar-brand">
          {/* White variant on the navy bar — UI_TASTE.md. Fixed height,
              object-fit contain, never stretched. */}
          <img src="/fkmt-logo-v2-white.png" alt="FKMT" className="topbar-logo" />
          <div className="app-name">ERP</div>
        </div>

        {/* Which books am I working in? Always visible, always current. */}
        <button
          className="context-badge"
          onClick={onSwitchContext}
          title="Switch company or financial year"
        >
          <span className="company">{session.companyName}</span>
          <span className={yearClosed ? 'fy closed' : 'fy'}>
            FY {session.financialYear}{yearClosed ? ' · CLOSED' : ''}
          </span>
        </button>

        <div className="spacer" />

        <div className="avatar" aria-hidden="true">{initials}</div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 500 }}>{session.name}</div>
          <div style={{ fontSize: 11, opacity: 0.6 }}>{session.userId}</div>
        </div>
        <button className="btn btn-secondary" onClick={onSignOut}>Sign out</button>
      </header>

      {/* A closed year is read-only. Saying so once, at the top, is kinder
          than letting someone fill in a document and fail on save. */}
      {yearClosed && (
        <div className="banner banner-warning">
          Financial year {session.financialYear} is closed. You can read and
          print history here, but not create or change records.
        </div>
      )}

      <div className="app-body">
        <nav className={drawerOpen ? 'sidebar open' : 'sidebar'}>
          {!rights.known && <div className="nav-soon">Loading…</div>}
          {rights.known && nav.map(group => (
            <div key={group.label}>
              <button
                type="button"
                className={openGroup === group.label
                  ? 'group-label open' : 'group-label'}
                aria-expanded={openGroup === group.label}
                onClick={() => setOpenGroup(
                  openGroup === group.label ? undefined : group.label)}
              >
                <span>{group.label}</span>
                <span className="chevron" aria-hidden="true">
                  {openGroup === group.label ? '–' : '+'}
                </span>
              </button>
              {openGroup === group.label && group.items.map(item => (
                <Fragment key={item.screen}>
                  {/* The masters a module owns, beneath the work that uses
                      them — rather than in one distant Masters menu. */}
                  {item.divider && (
                    <div className="nav-divider">{item.divider}</div>
                  )}
                  {item.soon ? (
                    // Shown so the shape of the system is visible; inert
                    // because a menu entry that leads nowhere wastes the
                    // reader's time and teaches them to distrust the menu.
                    <span className="nav-soon" title="Not built yet">
                      {item.label}
                    </span>
                  ) : (
                    <a
                      href={`#${item.screen}`}
                      className={screen === item.screen ? 'active' : undefined}
                      onClick={e => { e.preventDefault(); onNavigate(item.screen) }}
                    >
                      <span>{item.label}</span>
                      {item.count !== undefined && (
                        <span className="count">{item.count}</span>
                      )}
                    </a>
                  )}
                </Fragment>
              ))}
            </div>
          ))}
        </nav>

        <main className="content">{children}</main>
      </div>
    </div>
  )
}
